/* eslint-disable @typescript-eslint/no-unused-vars */
import { ApexOptions } from 'apexcharts';
import { useState, useCallback, useEffect } from 'react';

import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import ButtonBase from '@mui/material/ButtonBase';
import CardHeader from '@mui/material/CardHeader';
import Card, { CardProps } from '@mui/material/Card';

import { fData } from 'src/utils/format-number';
import InfoIcon from '@mui/icons-material/Info';
import Iconify from 'src/components/iconify';
import Chart, { useChart } from 'src/components/chart';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import { styled } from '@mui/material/styles';
import { card } from 'src/theme/overrides/components/card';

// ----------------------------------------------------------------------
const CHART_HEIGHT = 385;

const LEGEND_HEIGHT = 42;

const StyledChart = styled(Chart)(({ theme }) => ({
  height: CHART_HEIGHT,
  '& .apexcharts-canvas, .apexcharts-inner, svg, foreignObject': {
    height: `100% !important`,
  },
  '& .apexcharts-legend': {

    right: '-6px !important',
    borderTop: `dashed 1px ${theme.palette.divider}`,
    top: `calc(${CHART_HEIGHT - LEGEND_HEIGHT}px) !important`,
    justifyContent: 'center !important',
  },
}));

interface Props extends CardProps {
  title?: string;
  subheader?: string;
  chart: {
    labels: {
      [key: string]: string[];
    };
    colors?: string[];
    series: {
      type: string;
      data: {
        name: string;
        data: number[];
      }[];
    }[];
    options?: ApexOptions;
  };
}

export default function FileDataSActivity({ title, subheader, chart, ...other }: Props) {
  const { labels, colors, series, options } = chart;

  const popover = usePopover();

  const [seriesData, setSeriesData] = useState('Month');

  useEffect(() => {
    if (title === 'Summary Report') {
      setSeriesData('Year');
    }
  }, [title]);

  const chartOptions = useChart({
    chart: {
      stacked: true,
    },
    colors,
    stroke: {
      width: 0,
    },
    yaxis: {
      labels: {
        formatter: (value: string | number) => {
          // Ensure we are dealing with a number before we format it
          if (typeof value === 'number') {
            const val = new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
              minimumFractionDigits: 0,
            }).format(value);
            return ` ${val}`;
          }
          return value;
        },
      },
    },
    xaxis: {
      categories:
        (seriesData === 'Week' && labels.week) ||
        (seriesData === 'Month' && labels.month) ||
        labels.year,
    },
    tooltip: {
      y: {
        formatter: (value: number) => {
          if (typeof value === 'number') {
            return new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
              minimumFractionDigits: 0,
            }).format(value);
          }
          if (title === 'Summary Report') {
            return `${value} B`;
          }
          return `$ ${value}`;
        },
      },
    },
    legend: {
      floating: true,
      position: 'bottom',
      horizontalAlign: 'center',
    },
    plotOptions: {
      bar: {
        borderRadius: (seriesData === 'Week' && 8) || (seriesData === 'Month' && 6) || 0,
        columnWidth: (title === 'Summary Report' && '40%') || '20%',
      },
    },
    ...options,
  });

  const handleChangeSeries = useCallback(
    (newValue: string) => {
      popover.onClose();
      setSeriesData(newValue);
    },
    [popover]
  );
    if(title === 'It Resource Tower Stand'){
      return (
        <>
            {series.map((item) => (
              <Box key={item.type} sx={{ mt: 3, mx: 3 }}>
                {item.type === seriesData && (
                  <StyledChart
                    dir="ltr"
                    type="bar"
                    series={item.data}
                    options={chartOptions}
                    width="100%"
                    height={350}
                  />
                )}
              </Box>
            ))}
        </>
      );
    }
  return (
    <>
      <Card>
        <CardHeader
          title={title}
          subheader='(in Millions USD)'
        />

        {series.map((item) => (
          <Box key={item.type} sx={{ mt: 3, mx: 3 }}>
            {item.type === seriesData && (
              <StyledChart
                dir="ltr"
                type="bar"
                series={item.data}
                options={chartOptions}
                width="100%"
                height={(title === 'Summary Report' && 310)||310}
              />
            )}
          </Box>
        ))}
      </Card>

      <CustomPopover open={popover.open} onClose={popover.onClose} sx={{ width: 140 }}>
        {series.map((option) => (
          <MenuItem
            key={option.type}
            selected={option.type === seriesData}
            onClick={() => handleChangeSeries(option.type)}
          >
            {option.type}
          </MenuItem>
        ))}
      </CustomPopover>
    </>
  );
}
